import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  ArrowLeft,
  ArrowUpRight,
  CheckCircle2,
  ExternalLink,
  MapPin,
  XCircle,
} from 'lucide-react';
import { formatCurrency } from '@/lib/format';
import i18n from '@/lib/i18n';
import {
  usePlatformTenantDetail,
  useUpdateFeeOverride,
  type PlatformTenantDetail,
  type TenantLocationSummary,
  type RecentBooking,
} from '@/api/hooks/usePlatformTenantDetail';
import { TTLockCredentialsCard } from '@/components/platform/tenant/TTLockCredentialsCard';
import { ShellyCredentialsCard } from '@/components/platform/tenant/ShellyCredentialsCard';

export default function PlatformTenantDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: tenant, isLoading, isError, error } = usePlatformTenantDetail(id);

  if (!id) {
    return <NotFound onBack={() => navigate('/platform')} />;
  }
  if (isLoading) {
    return <LoadingState />;
  }
  if (isError || !tenant) {
    const status = (error as { status?: number } | null)?.status;
    if (status === 404) return <NotFound onBack={() => navigate('/platform')} />;
    return (
      <div className="px-6 py-10 text-sm text-red-700">
        Failed to load tenant. Please try again.
      </div>
    );
  }

  return (
    <div className="px-6 py-8 md:px-10 md:py-10 space-y-6">
      <button
        type="button"
        onClick={() => navigate('/platform')}
        className="inline-flex items-center gap-2 text-sm text-neutral-500 transition-colors hover:text-neutral-800"
      >
        <ArrowLeft className="h-4 w-4" aria-hidden="true" />
        Back to tenants
      </button>

      <HeaderCard tenant={tenant} />
      <StatsRow tenant={tenant} />
      <FeeOverrideCard tenant={tenant} />
      <HardwareCredentialsSection tenant={tenant} />
      <StripeConnectCard tenant={tenant} />
      <LocationsSection locations={tenant.locations} />
      <RecentBookingsCard bookings={tenant.recentBookings} currency={tenant.defaultCurrency ?? 'CAD'} />
      <QuickActionsCard />
    </div>
  );
}

function HardwareCredentialsSection({ tenant }: { tenant: PlatformTenantDetail }) {
  const { t } = useTranslation();
  return (
    <section className="space-y-3">
      <div>
        <h2 className="text-base font-semibold text-neutral-900">
          {t('platform_admin.tenant_detail.credentials.section_title')}
        </h2>
        <p className="mt-1 text-sm text-neutral-500">
          {t('platform_admin.tenant_detail.credentials.section_description')}
        </p>
      </div>
      <div className="grid gap-4 md:grid-cols-2">
        <TTLockCredentialsCard
          tenantId={tenant.id}
          isConfigured={tenant.ttlockCredentialsSet}
        />
        <ShellyCredentialsCard
          tenantId={tenant.id}
          isConfigured={tenant.shellyCredentialsSet}
        />
      </div>
    </section>
  );
}

// ── Sections ────────────────────────────────────────────

function HeaderCard({ tenant }: { tenant: PlatformTenantDetail }) {
  return (
    <section className="rounded-xl border border-neutral-200 bg-white p-6 shadow-sm">
      <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-neutral-900 md:text-3xl">
            {tenant.name}
          </h1>
          <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-neutral-500 font-mono">
            <span>{tenant.slug}</span>
            <span aria-hidden="true">·</span>
            <PlanBadge plan={tenant.planTier} />
            <span aria-hidden="true">·</span>
            <StatusBadge status={tenant.status} />
          </div>
          <dl className="mt-4 grid gap-x-8 gap-y-1 text-sm text-neutral-600 sm:grid-cols-2">
            <div className="flex gap-2">
              <dt className="text-neutral-400">Joined</dt>
              <dd>{formatDate(tenant.createdAt, i18n.language)}</dd>
            </div>
            <div className="flex gap-2">
              <dt className="text-neutral-400">Contact</dt>
              <dd>
                {tenant.contactEmail ? (
                  <>
                    {tenant.contactName ? `${tenant.contactName} · ` : ''}
                    <span className="font-mono text-[13px]">{tenant.contactEmail}</span>
                  </>
                ) : (
                  <span className="italic text-neutral-400">No admin assigned</span>
                )}
              </dd>
            </div>
            <div className="flex gap-2">
              <dt className="text-neutral-400">Currency</dt>
              <dd>{tenant.defaultCurrency ?? '—'}</dd>
            </div>
            <div className="flex gap-2">
              <dt className="text-neutral-400">Last activity</dt>
              <dd>{tenant.lastActivityAt ? formatDate(tenant.lastActivityAt, i18n.language) : '—'}</dd>
            </div>
          </dl>
        </div>
      </div>
    </section>
  );
}

function StatsRow({ tenant }: { tenant: PlatformTenantDetail }) {
  const currency = tenant.defaultCurrency ?? 'CAD';
  return (
    <section className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
      <StatCard
        label="Total revenue"
        value={formatCurrency(Number(tenant.totalRevenue ?? 0), currency, i18n.language)}
        sub={`${tenant.totalBookings.toLocaleString()} bookings`}
      />
      <StatCard
        label="Revenue this month"
        value={formatCurrency(Number(tenant.revenueThisMonth ?? 0), currency, i18n.language)}
        sub={`${tenant.bookingsThisMonth.toLocaleString()} bookings`}
      />
      <StatCard
        label="Platform fees"
        value={formatCurrency(Number(tenant.totalPlatformFeeCollected ?? 0), currency, i18n.language)}
        sub="all-time"
      />
      <StatCard
        label="Fees this month"
        value={formatCurrency(Number(tenant.platformFeeThisMonth ?? 0), currency, i18n.language)}
        sub="this calendar month"
      />
    </section>
  );
}

function FeeOverrideCard({ tenant }: { tenant: PlatformTenantDetail }) {
  const mutation = useUpdateFeeOverride(tenant.id);
  const [input, setInput] = useState<string>(Number(tenant.platformFeePercent).toFixed(2));

  useEffect(() => {
    setInput(Number(tenant.platformFeePercent).toFixed(2));
  }, [tenant.platformFeePercent]);

  const pct = Number(tenant.platformFeePercent);
  const isCustom = tenant.platformFeeOverride != null;
  const parsed = parseFloat(input);
  const disableSave =
    mutation.isPending ||
    Number.isNaN(parsed) ||
    parsed < 0 ||
    parsed > 25 ||
    Math.abs(parsed - pct) < 0.001;

  const onSave = () => {
    mutation.mutate(
      { resetToDefault: false, feePercent: Number(parsed.toFixed(2)) },
      {
        onSuccess: () =>
          toast.success(`Platform fee set to ${parsed.toFixed(2)}%`),
        onError: () => toast.error('Could not update platform fee'),
      },
    );
  };

  const onReset = () => {
    mutation.mutate(
      { resetToDefault: true },
      {
        onSuccess: () => toast.success('Platform fee reset to default'),
        onError: () => toast.error('Could not reset platform fee'),
      },
    );
  };

  return (
    <section className="rounded-xl border border-neutral-200 bg-white p-6 shadow-sm">
      <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
        <div>
          <h2 className="text-base font-semibold text-neutral-900">Platform fee</h2>
          <p className="mt-1 text-sm text-neutral-500">
            Charged on every booking. Changes take effect on the next booking Stripe processes.
          </p>
          <p className="mt-4 text-sm text-neutral-700">
            Current rate:{' '}
            <span className="font-mono font-medium text-neutral-900">{pct.toFixed(2)}%</span>{' '}
            <span className={isCustom ? 'text-[#C2713A]' : 'text-neutral-400'}>
              ({isCustom ? 'custom override' : 'platform default'})
            </span>
          </p>
        </div>

        <div className="flex flex-col gap-2 md:w-80">
          <label htmlFor="fee-input" className="text-xs font-medium text-neutral-500">
            New rate (%)
          </label>
          <div className="flex items-center gap-2">
            <input
              id="fee-input"
              type="number"
              step={0.01}
              min={0}
              max={25}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              className="w-28 rounded-md border border-neutral-200 bg-white px-3 py-2 text-sm font-mono text-neutral-900 focus-visible:border-[#C2713A] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C2713A]/30"
            />
            <button
              type="button"
              disabled={disableSave}
              onClick={onSave}
              className="inline-flex items-center rounded-md bg-[#C2713A] px-3 py-2 text-sm font-semibold text-white transition-colors hover:bg-[#a95f2f] disabled:cursor-not-allowed disabled:opacity-50"
            >
              Save
            </button>
            <button
              type="button"
              disabled={mutation.isPending || !isCustom}
              onClick={onReset}
              className="inline-flex items-center rounded-md border border-neutral-200 px-3 py-2 text-sm font-medium text-neutral-700 transition-colors hover:bg-neutral-50 disabled:cursor-not-allowed disabled:opacity-40"
            >
              Reset
            </button>
          </div>
          <p className="text-[11px] text-neutral-400">
            Range 0–25%. Default is {tenant.defaultPlatformFee.toFixed(2)}%.
          </p>
        </div>
      </div>
    </section>
  );
}

function StripeConnectCard({ tenant }: { tenant: PlatformTenantDetail }) {
  const { stripeConnectStatus: status, stripeAccountId } = tenant;
  const label =
    status === 'CONNECTED' ? 'Connected' : status === 'PENDING' ? 'Onboarding' : 'Not started';
  const badgeCls =
    status === 'CONNECTED'
      ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
      : status === 'PENDING'
        ? 'bg-amber-50 text-amber-700 border-amber-200'
        : 'bg-neutral-100 text-neutral-600 border-neutral-200';

  return (
    <section className="rounded-xl border border-neutral-200 bg-white p-6 shadow-sm">
      <div className="flex items-start justify-between">
        <div>
          <h2 className="text-base font-semibold text-neutral-900">Stripe Connect</h2>
          <p className="mt-1 text-sm text-neutral-500">
            Status reflects the operator's Stripe account. Payouts and charges gated on
            this.
          </p>
        </div>
        <span
          className={`inline-flex items-center rounded-full border px-2.5 py-1 text-xs font-medium ${badgeCls}`}
        >
          {label}
        </span>
      </div>

      <dl className="mt-4 grid gap-x-8 gap-y-2 text-sm sm:grid-cols-2">
        <div className="flex gap-2">
          <dt className="text-neutral-400">Account</dt>
          <dd className="font-mono text-[13px] text-neutral-900">
            {stripeAccountId ?? '—'}
          </dd>
        </div>
        <div className="flex gap-2">
          <dt className="text-neutral-400">Charges</dt>
          <dd>
            {tenant.stripeChargesEnabled ? (
              <CheckCircle2 className="inline h-4 w-4 text-emerald-600" aria-label="Enabled" />
            ) : (
              <XCircle className="inline h-4 w-4 text-neutral-400" aria-label="Disabled" />
            )}
          </dd>
        </div>
        <div className="flex gap-2">
          <dt className="text-neutral-400">Payouts</dt>
          <dd>
            {tenant.stripePayoutsEnabled ? (
              <CheckCircle2 className="inline h-4 w-4 text-emerald-600" aria-label="Enabled" />
            ) : (
              <XCircle className="inline h-4 w-4 text-neutral-400" aria-label="Disabled" />
            )}
          </dd>
        </div>
        <div className="flex gap-2">
          <dt className="text-neutral-400">Details submitted</dt>
          <dd>
            {tenant.stripeDetailsSubmitted ? (
              <CheckCircle2 className="inline h-4 w-4 text-emerald-600" aria-label="Submitted" />
            ) : (
              <XCircle className="inline h-4 w-4 text-neutral-400" aria-label="Not submitted" />
            )}
          </dd>
        </div>
      </dl>

      {stripeAccountId ? (
        <a
          href={`https://dashboard.stripe.com/connect/accounts/${stripeAccountId}`}
          target="_blank"
          rel="noreferrer"
          className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-[#C2713A] hover:underline"
        >
          Open in Stripe dashboard
          <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
        </a>
      ) : (
        <p className="mt-4 text-xs text-neutral-500">
          Operator needs to complete Stripe Connect onboarding from their admin panel.
        </p>
      )}
    </section>
  );
}

function LocationsSection({ locations }: { locations: TenantLocationSummary[] }) {
  return (
    <section className="space-y-3">
      <h2 className="text-base font-semibold text-neutral-900">
        Locations <span className="text-neutral-400">({locations.length})</span>
      </h2>
      <div className="grid gap-4 md:grid-cols-2">
        {locations.map((loc) => (
          <LocationCard key={loc.id} loc={loc} />
        ))}
        {locations.length === 0 && (
          <div className="rounded-xl border border-dashed border-neutral-200 p-6 text-sm text-neutral-400">
            No locations for this tenant yet.
          </div>
        )}
      </div>
    </section>
  );
}

function LocationCard({ loc }: { loc: TenantLocationSummary }) {
  return (
    <article className="rounded-xl border border-neutral-200 bg-white p-5 shadow-sm">
      <div className="flex items-start justify-between">
        <div>
          <h3 className="font-semibold text-neutral-900">{loc.name}</h3>
          <p className="mt-1 flex items-start gap-1 text-xs text-neutral-500">
            <MapPin className="mt-0.5 h-3 w-3 flex-none" aria-hidden="true" />
            <span>
              {loc.address && <span>{loc.address}, </span>}
              {loc.city}
              {loc.province && `, ${loc.province}`}
            </span>
          </p>
        </div>
        <span
          className={
            'inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-medium ' +
            (loc.active
              ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
              : 'bg-neutral-100 text-neutral-500 border-neutral-200')
          }
        >
          {loc.active ? 'Active' : 'Inactive'}
        </span>
      </div>

      <p className="mt-3 text-xs text-neutral-500">
        <span className="font-mono text-neutral-900">{loc.unitCount}</span> units ·{' '}
        <span className="font-mono text-neutral-900">{loc.onlineUnitCount}</span> online ·{' '}
        <span className="font-mono text-neutral-900">{loc.sportCodes.length}</span> sports
      </p>

      {loc.sportCodes.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-1.5">
          {loc.sportCodes.map((code) => (
            <span
              key={code}
              className="rounded-full border border-neutral-200 bg-neutral-50 px-2 py-0.5 text-[11px] font-mono text-neutral-600"
            >
              {code}
            </span>
          ))}
        </div>
      )}

      <Link
        to={`/venue/${loc.slug}`}
        target="_blank"
        rel="noreferrer"
        className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-[#C2713A] hover:underline"
      >
        View venue page
        <ArrowUpRight className="h-3.5 w-3.5" aria-hidden="true" />
      </Link>
    </article>
  );
}

function RecentBookingsCard({
  bookings,
  currency,
}: {
  bookings: RecentBooking[];
  currency: string;
}) {
  return (
    <section className="rounded-xl border border-neutral-200 bg-white shadow-sm">
      <header className="border-b border-neutral-100 px-6 py-4">
        <h2 className="text-base font-semibold text-neutral-900">Recent bookings</h2>
        <p className="mt-0.5 text-xs text-neutral-500">Latest 10, most recent first.</p>
      </header>
      {bookings.length === 0 ? (
        <div className="px-6 py-10 text-center text-sm text-neutral-400">
          No bookings yet for this tenant.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-neutral-200 text-sm">
            <thead className="bg-neutral-50 text-xs font-semibold uppercase tracking-wide text-neutral-500">
              <tr>
                <th className="px-4 py-2 text-left">When</th>
                <th className="px-4 py-2 text-left">Location</th>
                <th className="px-4 py-2 text-left">Sport</th>
                <th className="px-4 py-2 text-left">Unit</th>
                <th className="px-4 py-2 text-left">Customer</th>
                <th className="px-4 py-2 text-left">Status</th>
                <th className="px-4 py-2 text-right">Amount</th>
                <th className="px-4 py-2 text-right">Fee</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100">
              {bookings.map((b) => (
                <tr key={b.id}>
                  <td className="whitespace-nowrap px-4 py-2 text-neutral-500">
                    {formatDate(b.startTime, i18n.language)}
                  </td>
                  <td className="px-4 py-2 text-neutral-700">{b.locationName ?? '—'}</td>
                  <td className="px-4 py-2 text-neutral-700">{b.sportName ?? '—'}</td>
                  <td className="px-4 py-2 text-neutral-700">{b.unitName ?? '—'}</td>
                  <td className="px-4 py-2 font-mono text-[13px] text-neutral-700">
                    {b.customerEmail ?? '—'}
                  </td>
                  <td className="px-4 py-2">
                    <BookingStatusBadge status={b.status} />
                  </td>
                  <td className="whitespace-nowrap px-4 py-2 text-right font-mono text-neutral-900">
                    {formatCurrency(Number(b.totalPrice ?? 0), b.currency ?? currency, i18n.language)}
                  </td>
                  <td className="whitespace-nowrap px-4 py-2 text-right font-mono text-neutral-700">
                    {formatCurrency(Number(b.platformFee ?? 0), b.currency ?? currency, i18n.language)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function QuickActionsCard() {
  return (
    <section className="rounded-xl border border-dashed border-neutral-200 bg-white p-6 text-sm text-neutral-500">
      <h2 className="text-sm font-semibold text-neutral-700">Quick actions</h2>
      <p className="mt-1 text-xs">
        Toggle public listing and suspend-operator controls ship in Sprint 7 Evening 3.
      </p>
    </section>
  );
}

// ── Bits ────────────────────────────────────────────────

function StatCard({
  label,
  value,
  sub,
}: {
  label: string;
  value: string;
  sub?: string;
}) {
  return (
    <div className="rounded-xl border border-neutral-200 bg-white p-5 shadow-sm">
      <p className="text-[11px] font-mono uppercase tracking-[0.16em] text-neutral-400">
        {label}
      </p>
      <p className="mt-2 font-mono text-2xl font-semibold text-neutral-900">{value}</p>
      {sub && <p className="mt-1 text-[11px] text-neutral-500">{sub}</p>}
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const styles: Record<string, string> = {
    ACTIVE: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    PENDING: 'bg-amber-50 text-amber-700 border-amber-200',
    SUSPENDED: 'bg-red-50 text-red-700 border-red-200',
  };
  const cls = styles[status] ?? 'bg-neutral-100 text-neutral-600 border-neutral-200';
  return (
    <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium ${cls}`}>
      {status.charAt(0) + status.slice(1).toLowerCase()}
    </span>
  );
}

function PlanBadge({ plan }: { plan: string | null }) {
  if (!plan) return <span className="text-neutral-400">—</span>;
  return (
    <span className="inline-flex items-center rounded-full border border-[#C2713A]/30 bg-[#C2713A]/10 px-2 py-0.5 text-xs font-medium text-[#C2713A]">
      {plan}
    </span>
  );
}

function BookingStatusBadge({ status }: { status: string }) {
  const styles: Record<string, string> = {
    CONFIRMED: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    ACTIVE: 'bg-blue-50 text-blue-700 border-blue-200',
    COMPLETED: 'bg-neutral-100 text-neutral-600 border-neutral-200',
    CANCELLED: 'bg-red-50 text-red-700 border-red-200',
    NO_SHOW: 'bg-amber-50 text-amber-700 border-amber-200',
    PENDING: 'bg-amber-50 text-amber-700 border-amber-200',
    EXPIRED: 'bg-neutral-100 text-neutral-500 border-neutral-200',
  };
  const cls = styles[status] ?? 'bg-neutral-100 text-neutral-600 border-neutral-200';
  return (
    <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-medium ${cls}`}>
      {status.replace('_', ' ')}
    </span>
  );
}

function LoadingState() {
  return (
    <div className="px-6 py-10 space-y-4">
      <div className="h-8 w-40 animate-pulse rounded bg-neutral-100" />
      <div className="h-28 animate-pulse rounded-xl bg-neutral-100" />
      <div className="grid gap-4 md:grid-cols-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="h-24 animate-pulse rounded-xl bg-neutral-100" />
        ))}
      </div>
      <div className="h-40 animate-pulse rounded-xl bg-neutral-100" />
    </div>
  );
}

function NotFound({ onBack }: { onBack: () => void }) {
  return (
    <div className="mx-auto max-w-md px-6 py-20 text-center">
      <h1 className="text-xl font-semibold text-neutral-900">Tenant not found</h1>
      <p className="mt-2 text-sm text-neutral-500">
        This operator doesn't exist (or was removed). Head back to the tenants list.
      </p>
      <button
        type="button"
        onClick={onBack}
        className="mt-6 inline-flex items-center rounded-md bg-[#C2713A] px-4 py-2 text-sm font-semibold text-white hover:bg-[#a95f2f]"
      >
        Back to tenants
      </button>
    </div>
  );
}

// Local helper — distinct from @/lib/format's formatDate (which formats
// long weekday + month). This one is year + month-short + day for the
// platform-admin tenant tables and uses the operator's active locale.
function formatDate(iso: string, lang: string) {
  const d = new Date(iso);
  return d.toLocaleDateString(lang.startsWith('fr') ? 'fr-CA' : 'en-CA', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}
